import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useResponsive } from "./responsive";
import { useTranslation } from "../i18n";

/**
 * Popover anchored to a toolbar button on desktop, bottom sheet on mobile.
 * Closes on outside click or Escape. Used by the emoji and GIF keyboards.
 */
const AnchoredPopover = ({ anchorRef, onClose, title, className = "", children }) => {
  const { t } = useTranslation("common");
  const { isMobile } = useResponsive();
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useLayoutEffect(() => {
    if (isMobile) return undefined;
    const place = () => {
      const trigger = anchorRef?.current;
      const el = popRef.current;
      if (!trigger || !el) return;
      const r = trigger.getBoundingClientRect();
      const margin = 8;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      // Left-align to the trigger; prefer below, flip above when out of room.
      const left = Math.min(
        Math.max(r.left, margin),
        Math.max(window.innerWidth - w - margin, margin)
      );
      let top = r.bottom + margin;
      if (top + h > window.innerHeight - margin) top = Math.max(r.top - h - margin, margin);
      setPos({ left, top });
    };
    place();
    // Content can load in after mount, so re-place whenever it resizes.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(place) : null;
    if (ro && popRef.current) ro.observe(popRef.current);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [isMobile, anchorRef]);

  if (isMobile) {
    return createPortal(
      <div
        className="fixed inset-0 z-[100] bg-black/40 flex items-end menu-backdrop-in"
        onClick={onClose}
      >
        <div
          className="w-full bg-white rounded-t-2xl shadow-xl pt-2 border-t border-gray-200 max-h-[75vh] overflow-hidden flex flex-col menu-sheet-in"
          style={{ paddingBottom: "max(8px, env(safe-area-inset-bottom))" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-3 pb-1">
            <span className="text-sm font-medium text-slate-700">{title}</span>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("actions.close")}
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {children}
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[100]" onClick={onClose}>
      <div
        ref={popRef}
        role="dialog"
        aria-label={title}
        className={`fixed rounded-xl shadow-lg overflow-hidden menu-pop-in ${className}`}
        style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export default AnchoredPopover;
